import { ApiClient } from '../lib/api';
import { z } from 'zod';
import {
  QuoteTemplate,
  CreateQuoteTemplate,
  UpdateQuoteTemplate,
  QuoteTemplateSchema,
  CreateQuoteTemplateSchema,
  UpdateQuoteTemplateSchema,
  QuoteTemplateSectionApiSchema,
} from '../models/quote-template';
import { transformIncomingLineItemTypes, transformOutgoingLineItemTypes } from '../lib/transformations';
import { 
  QuoteSection
} from '../models/line-item';
import {
  QuoteTemplateSectionsApiSchema,
  QuoteTemplateSectionApi
} from '../models/quote-template';

export class QuoteTemplateService {
  private api: ApiClient;

  constructor(baseURL: string, stage: string) {
    this.api = new ApiClient(`${baseURL}/${stage}/quote-service-${stage}`, stage);
  }

  async getAllQuoteTemplates(wotypes?: string): Promise<QuoteTemplate[]> {
    const quoteTemplates = await this.api.request(
      { method: 'GET', url: '/quote-templates' + (wotypes?.length ? `?wotypes=${wotypes}` : '') },
      QuoteTemplateSchema.array()
    );
    return quoteTemplates.map(transformIncomingLineItemTypes);
  }

  async getQuoteTemplateById(id: number): Promise<QuoteTemplate> {
    const quoteTemplate = await this.api.request(
      { method: 'GET', url: `/quote-templates/${id}` },
      QuoteTemplateSchema
    );
    return transformIncomingLineItemTypes(quoteTemplate);
  }

  async createQuoteTemplate(data: CreateQuoteTemplate): Promise<QuoteTemplate> {
    const quoteTemplate = await this.api.request(
      {
        method: 'POST',
        url: '/quote-templates',
        data: transformOutgoingLineItemTypes(CreateQuoteTemplateSchema.parse(data) as QuoteTemplate)
      },
      QuoteTemplateSchema
    );
    return transformIncomingLineItemTypes(quoteTemplate);
  }

  async updateQuoteTemplate(id: number, data: UpdateQuoteTemplate): Promise<QuoteTemplate> {
    const quoteTemplate = await this.api.request(
      {
        method: 'PUT',
        url: `/quote-templates/${id}`,
        data: transformOutgoingLineItemTypes(UpdateQuoteTemplateSchema.parse(data) as QuoteTemplate)
      },
      QuoteTemplateSchema
    );
    return transformIncomingLineItemTypes({
      ...quoteTemplate,
      lineItems: quoteTemplate.lineItems?.map((item, index) => ({
        ...item,
        ntitrackingid: data.lineItems?.[index]?.ntitrackingid || crypto.randomUUID()
      }))
    });
  }

  async deleteQuoteTemplate(id: number): Promise<void> {
    await this.api.request(
      { method: 'DELETE', url: `/quote-templates/${id}` },
      z.void()
    );
  }

  // New methods for quote template sections
  async getQuoteTemplateSections(templateId: number): Promise<QuoteSection[]> {
    try {
      const response = await this.api.request(
        { method: 'GET', url: `/quote-templates/${templateId}/sections` },
        QuoteTemplateSectionsApiSchema
      );

      return response.map(section => ({
        id: section.ntsid,
        trackingId: section.ntstrackingid ?? crypto.randomUUID(),
        name: section.ntsname,
        order: section.ntsorder,
        wotype: section.ntswotype,
        crewId: section.ntscruid,
        isCollapsed: false
      }));
    } catch (error) {
      console.error("Error fetching template sections:", error);
      return [];
    }
  }

  async updateQuoteTemplateSections(templateId: number, sections: QuoteSection[]): Promise<void> {
    const apiSections: QuoteTemplateSectionApi[] = sections.map(section => ({
      ntsid: section.id,
      ntsntid: templateId,
      ntsname: section.name,
      ntsorder: section.order,
      ntswotype: section.wotype,
      ntscruid: section.crewId ?? null,
      ntstrackingid: section.trackingId,
    }));

    await this.api.request(
      {
        method: 'PUT',
        url: `/quote-templates/${templateId}/sections`,
        data: apiSections
      },
      QuoteTemplateSectionApiSchema.array()
    );
  }
} 
